// src/tools/connectors.ts
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import type { ConnectorManager } from "@skaile/workspaces/connectors";
import type { ToolResult } from "../types.js";
import { err, ok, okJson } from "../types.js";

// ── Mountable-connector helpers ──────────────────────────────────────────────
//
// The unified ConnectorManager owns mountable (filesystem-face) connectors too.
// A connector is mountable when its `listConnectors()` summary carries a
// `mountPath`. The `mount*` helpers below project that subset for the legacy
// `mount_*` tool surface.

/**
 * A connector summary that carries a projected filesystem path. Unlike the raw
 * `listConnectors()` summary, `mountPath` is non-optional here — `listMountable()`
 * runtime-filters to exactly those entries before narrowing to this type.
 */
type MountableConnector = ReturnType<ConnectorManager["listConnectors"]>[number] & {
  mountPath: string;
};

/** Return the mountable (filesystem-face) connectors from a manager. */
function listMountable(manager: ConnectorManager): MountableConnector[] {
  return manager
    .listConnectors()
    .filter((c): c is MountableConnector => typeof c.mountPath === "string");
}

/**
 * List all mountable (filesystem-face) connectors declared in `skaile.yaml`
 * with their current mount status.
 *
 * @param manager - Active `ConnectorManager` or `null` when not configured.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function mountList(manager: ConnectorManager | null): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    return okJson(listMountable(manager));
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Sync a mountable connector — pull the latest content from its remote source.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param mountId - Connector ID declared in `skaile.yaml`.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function mountSync(
  manager: ConnectorManager | null,
  mountId: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const result = await manager.sync(mountId);
    return okJson(result);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Get the current status and metadata for a single mountable connector.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param mountId - Connector ID declared in `skaile.yaml`.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function mountStatus(
  manager: ConnectorManager | null,
  mountId: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const mount = listMountable(manager).find((v) => v.id === mountId);
    if (!mount) return err(`Mount not found: "${mountId}"`);
    return okJson(mount);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Read a file from within a mountable connector's projected directory by its
 * relative path.
 *
 * @param manager  - Active `ConnectorManager` or `null`.
 * @param mountId  - Connector ID declared in `skaile.yaml`.
 * @param filePath - Relative path within the projected mount directory.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function mountRead(
  manager: ConnectorManager | null,
  mountId: string,
  filePath: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const mount = listMountable(manager).find((v) => v.id === mountId);
    if (!mount) return err(`Mount not found: "${mountId}"`);
    const content = await readFile(join(mount.mountPath, filePath), "utf-8");
    return ok(content);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * List all connectors registered with the `ConnectorManager`.
 *
 * Returns `{ connected, failed }` — `connected` from `manager.listConnectors()`,
 * `failed` from `manager.listFailedConnectors()`. A connector that threw during
 * `connect()` never enters the active map, so it is absent from the first list
 * entirely; without the second one an agent driven through MCP or OMP has no
 * way to learn that a mount it is about to write into is dead. Returns an error
 * result when no manager is configured (i.e. `skaile.yaml` has no `connectors`
 * section).
 *
 * @param manager - Active `ConnectorManager` or `null` when not configured.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorList(manager: ConnectorManager | null): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    // Defensive: a host may pass an older manager or a test double.
    const failed =
      typeof manager.listFailedConnectors === "function" ? manager.listFailedConnectors() : [];
    return okJson({ connected: manager.listConnectors(), failed });
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Read content from a connector at the given path.
 *
 * Delegates to `manager.read(id, path)`.  Returns an error result when the
 * path yields `null` (not found) or the manager is absent.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param id      - Connector ID declared in `skaile.yaml`.
 * @param path    - Resource path within the connector (connector-specific format).
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorRead(
  manager: ConnectorManager | null,
  id: string,
  path: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const content = await manager.read(id, path);
    if (content === null) return err(`No content at path "${path}" in connector "${id}"`);
    return okJson(content);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Write string content to a connector at the given path.
 *
 * Delegates to `manager.write(id, path, { data: content })`.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param id      - Connector ID declared in `skaile.yaml`.
 * @param path    - Destination path within the connector.
 * @param content - String payload to write.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorWrite(
  manager: ConnectorManager | null,
  id: string,
  path: string,
  content: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    await manager.write(id, path, { data: content });
    return ok(`Written to "${path}" in connector "${id}"`);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Search a connector's data using a query string.
 *
 * Delegates to `manager.search(id, query)` and returns the result set as JSON.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param id      - Connector ID declared in `skaile.yaml`.
 * @param query   - Free-text or structured query (connector-specific format).
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorSearch(
  manager: ConnectorManager | null,
  id: string,
  query: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const results = await manager.search(id, query);
    return okJson(results);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Execute a named operation on a connector.
 *
 * Delegates to `manager.executeOp(id, operation, args)`.  Operations and
 * their argument shapes are connector-specific; use {@link connectorDescribe}
 * to discover what operations a connector supports.
 *
 * @param manager   - Active `ConnectorManager` or `null`.
 * @param id        - Connector ID declared in `skaile.yaml`.
 * @param operation - Operation name (from the connector's `RuntimeSkillDescriptor`).
 * @param args      - Named arguments for the operation.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorExec(
  manager: ConnectorManager | null,
  id: string,
  operation: string,
  args: Record<string, unknown>,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const result = await manager.executeOp(id, operation, args);
    return ok(result);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Load the rendered skill body for a connector.
 *
 * Delegates to `manager.loadSkill(id)` and returns the skill content as
 * plain text.  Useful when the agent needs the connector's operations and
 * usage instructions injected as prompt context.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param id      - Connector ID declared in `skaile.yaml`.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorLoadSkill(
  manager: ConnectorManager | null,
  id: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const skill = manager.loadSkill(id);
    return ok(skill);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Describe a connector's capabilities via its `RuntimeSkillDescriptor`.
 *
 * Finds the matching skill descriptor from `manager.listSkills()` and returns
 * it as JSON.  The descriptor includes the connector's operations, argument
 * shapes, and usage instructions.
 *
 * @param manager - Active `ConnectorManager` or `null`.
 * @param id      - Connector ID declared in `skaile.yaml`.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function connectorDescribe(
  manager: ConnectorManager | null,
  id: string,
): Promise<ToolResult> {
  if (!manager) return err("No connectors configured in skaile.yaml");
  try {
    const skills = manager.listSkills();
    const descriptor = skills.find((s) => s.id === id);
    if (!descriptor) return err(`No skill descriptor for connector "${id}"`);
    return okJson(descriptor);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}
